/**
 * Nueva campaña · «Con los grupos de tu Público» (`/activa/segmentaciones/desde-publico`, decisión de Alan del
 * 01/11/2026). Del equipo.
 *
 * - No segmenta: usa los grupos que ya tiene cada persona en el Público, los de la última segmentación. Dice de cuál
 *   salen (la fecha y la base) y cuántas personas van en cada grupo: sin el control de esa campaña (su medición no se
 *   toca) y sin quien ya no está en la base.
 * - El control se sortea de nuevo: arranca en el % de Descuentos de la marca y se puede cambiar para esta campaña.
 * - «Crear la campaña» la deja lista y sigue en Grupos, como el Excel; de ahí, Mensajes (donde cada grupo elige su canal y
 *   su mensaje) y el envío, como siempre.
 * - Sin grupos (nunca se segmentó, o de sus grupos no queda nadie en la base), un vacío que lleva a «Con segmentación».
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Users } from "lucide-react";
import { fecha, num } from "../../campana-nueva/utils/format";
import { MarcaDeTrabajo } from "../../campana-nueva/components/MarcaDeTrabajo";
import { R_SUBIR_BASE } from "../../base-ventas/logic/base";
import { usePedido } from "../../emails/hooks/usePedido";
import { llamar } from "../../wa-oficial/components/base/comun";
import { ErrorTarjeta, Esqueleto } from "../../wa-oficial/components/estadisticas/comun";
import { textoError } from "../../wa-oficial/logic/respuesta";
import { Pagina } from "../../../components/activa/Pagina";
import { segmentacionesApi } from "../api";
import { Antetitulo, PasosCampana, btnPrimario } from "../components/PasosCampana";
import { CONTROL_POR_DEFECTO, OPCIONES_CONTROL, R_NUEVA_CAMPANA, nombreDelGrupo, rutaGrupos, rutaSegmentar } from "../logic/segmentar";
import type { GruposDelPublico, Segmentacion } from "../types";

export const TITULO_DESDE_PUBLICO = "Con los grupos de tu Público";
export const BAJADA_DESDE_PUBLICO = "Usás los grupos que ya armó DEENEX con tu base, sin volver a segmentar. Cada grupo elige su canal y su mensaje.";

const personas = (n: number) => `${num(n)} ${n === 1 ? "persona" : "personas"}`;

function Encabezado() {
  return (
    <div className="space-y-1.5">
      <Antetitulo>Paso 1 de 4</Antetitulo>
      <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{TITULO_DESDE_PUBLICO}</h1>
      <p className="text-sm sm:text-base text-slate-600">{BAJADA_DESDE_PUBLICO}</p>
    </div>
  );
}

/** Sin grupos en el Público: se arman con «Con segmentación». Sin base, primero se sube. */
function Vacio({ code, texto }: { code: string; texto: string }) {
  const sinBase = code === "SIN_BASE";
  return (
    <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid={sinBase ? "sin-base" : "sin-grupos"}>
      <p className="flex items-center gap-2 text-base font-semibold text-slate-900">
        <Users size={18} className="flex-none text-violet-600" aria-hidden="true" />
        {sinBase ? "Primero subí tu base de ventas" : "Tu Público todavía no tiene grupos"}
      </p>
      <p className="text-sm text-slate-600">{texto}</p>
      <Link to={sinBase ? R_SUBIR_BASE : R_NUEVA_CAMPANA} className={btnPrimario}>
        {sinBase ? "Subir base de datos" : "Armar con segmentación"}
      </Link>
    </div>
  );
}

function Grupos({ d, recargar }: { d: GruposDelPublico; recargar: () => void }) {
  const navigate = useNavigate();
  // el % elegido acá; mientras no se elija otro, el de Descuentos que mandó el servidor (como en Segmentar)
  const [elegido, setElegido] = useState<number | null>(null);
  const pct = elegido ?? (OPCIONES_CONTROL.includes(d.pctControl) ? d.pctControl : CONTROL_POR_DEFECTO);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<{ texto: string; otra?: string } | null>(null);
  const otraBase = d.base.versionNumero !== d.origen.versionNumero;

  const crear = async () => {
    setCreando(true);
    setError(null);
    const l = await llamar<Segmentacion>(() => segmentacionesApi.crearDesdePublico(pct, d.origen.id));
    setCreando(false);
    if (l.ok) return navigate(rutaGrupos(l.data.id));
    setError({ texto: textoError(l), otra: typeof l.extra.segmentacionId === "string" ? l.extra.segmentacionId : undefined });
    // el Público ya tiene otros grupos: se muestran los nuevos para revisarlos antes de crear
    if (l.code === "GRUPOS_CAMBIARON") recargar();
  };

  return (
    <section aria-labelledby="grupos-titulo" className="space-y-4">
      <h2 id="grupos-titulo" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
        Tus grupos
      </h2>
      <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
        <div className="space-y-1 p-4" data-testid="origen">
          <p className="font-semibold text-slate-900 break-words">De «{d.origen.nombre}»</p>
          <p className="text-sm text-slate-600">
            Segmentación del {fecha(d.origen.terminadaAt ?? undefined)} · base v{num(d.origen.versionNumero)}
          </p>
          {otraBase && <p className="text-sm text-slate-600">Hoy usás la base v{num(d.base.versionNumero)}: van quienes siguen en ella.</p>}
        </div>
        <ul aria-label="Grupos" className="divide-y divide-slate-100">
          {d.grupos.map((g, i) => (
            <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
              <span className="min-w-0 break-words font-medium text-slate-900">{nombreDelGrupo(g, i)}</span>
              <span className={`text-sm tabular-nums ${g.personas ? "text-slate-600" : "text-slate-400"}`}>{g.personas ? personas(g.personas) : "Nadie sigue en tu base: no va"}</span>
            </li>
          ))}
        </ul>
        <div className="space-y-1 p-4">
          <p className="text-sm font-semibold text-slate-900" data-testid="total">
            {personas(d.personas)} en {d.grupos.filter((g) => g.personas).length} {d.grupos.filter((g) => g.personas).length === 1 ? "grupo" : "grupos"}
          </p>
          {d.controlAnterior > 0 && (
            <p className="text-sm text-slate-600">
              No van {d.controlAnterior === 1 ? "la persona" : `las ${num(d.controlAnterior)} personas`} del control de «{d.origen.nombre}»: así su medición no se toca.
            </p>
          )}
          {d.noEstan > 0 && (
            <p className="text-sm text-slate-600">
              {personas(d.noEstan)} de esos grupos ya no {d.noEstan === 1 ? "está" : "están"} en tu base y {d.noEstan === 1 ? "queda" : "quedan"} afuera.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3 p-4">
          <label htmlFor="pct-control" className="min-w-0">
            <span className="block font-semibold text-slate-900">Grupo de control</span>
            <span className="block text-sm text-slate-600">{pct}% de cada grupo no recibe nada, para medir. Se sortea de nuevo para esta campaña.</span>
          </label>
          <select
            id="pct-control"
            value={pct}
            onChange={(x) => setElegido(Number(x.target.value))}
            className="min-h-[44px] rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
          >
            {OPCIONES_CONTROL.map((o) => (
              <option key={o} value={o}>
                {o}%
              </option>
            ))}
          </select>
        </div>
      </div>
      {error && (
        <div role="alert" className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error.texto}{" "}
          {error.otra && (
            <Link to={rutaSegmentar(error.otra)} className="font-semibold underline">
              Ver la que se está armando
            </Link>
          )}
        </div>
      )}
      <button type="button" className={btnPrimario} disabled={creando} onClick={() => void crear()}>
        {creando && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
        Crear la campaña
      </button>
    </section>
  );
}

export default function DesdePublicoPage() {
  const pedido = usePedido<GruposDelPublico>(() => segmentacionesApi.desdePublico(), "desde-publico");
  const d = pedido.dato;
  const vacio = !d && !pedido.cargando && (pedido.code === "SIN_GRUPOS" || pedido.code === "SIN_BASE");
  return (
    // el mismo contenedor que Segmentar, Grupos y Mensajes: el título y los pasos no saltan al pasar de uno a otro
    <Pagina angosta>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-slate-900">Nueva campaña</p>
        <MarcaDeTrabajo accion="Armando la campaña de" />
      </div>
      <PasosCampana actual={0} rutas={[null, null, null]} />
      <div className="space-y-6">
        <Encabezado />
        {pedido.cargando && !d && <Esqueleto alto="h-64" etiqueta="Cargando los grupos de tu Público" />}
        {vacio && <Vacio code={pedido.code} texto={pedido.error} />}
        {!d && !pedido.cargando && pedido.error && !vacio && <ErrorTarjeta texto={pedido.error} onReintentar={pedido.recargar} />}
        {d && <Grupos d={d} recargar={pedido.recargar} />}
      </div>
    </Pagina>
  );
}
